import React, {
  useMemo,
  useState,
  useEffect,
  useCallback
} from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Flex,
  Table,
  Button,
  TableRow,
  Paragraph,
  TableBody,
  TableCell,
  TableHead,
  Pagination,
  TableActions,
  LoadingContainer,
  OrderableTableRow
} from '@sopranodesign/pulse-components';
import { useQuery } from '@sopranodesign/pulse-hooks';
import { usePaginationReducer, useOrderableTableReducer } from '@sopranodesign/pulse-components';
import {
  useAuth,
  PermissionFilter
} from '@sopranodesign/pulse-auth';

import { StateLevel } from '../';

const Resource = ({ site, folder, state }) => {
  const { hasPermission } = useAuth();
  const canViewTag = hasPermission('view generative ai tag');
  const canEditTag = hasPermission('edit generative ai tag');
  const canEditResource = hasPermission('edit generative ai resource');

  const {
    crawlResources,
    setActiveModal,
    setCrawlResources,
    setSelectedResource
  } = state;

  const [shouldRefetch, setShouldRefetch] = useState(false);

  const order = useOrderableTableReducer({ id: 'resources-sharepoint' });
  const pagination = usePaginationReducer({ id: 'resources-sharepoint' });

  const queryKey = useMemo(
    () => [
      `generative-ai/sharepoint/site/${site.id}/folder/${folder.id}/resource`,
      { ...pagination.params, ...order.params }
    ],
    [
      site.id,
      folder.id,
      order.params,
      pagination.params
    ]
  );

  const enabled = Boolean(site.id) && Boolean(folder.id);

  const {
    error,
    isLoading,
    data: resources
  } = useQuery({
    queryKey,
    enabled,
    refetchInterval: crawlResources ? 5000 : false
  });

  useEffect(() => {
    const hasPending = resources?.has_pending === true;
    setShouldRefetch(prev => (prev !== hasPending ? hasPending : prev));

    if (hasPending && !crawlResources) {
      setCrawlResources(true);
    } else if (!hasPending && crawlResources) {
      setCrawlResources(false);
    }
  }, [crawlResources, setCrawlResources, resources?.has_pending]);

  const handleTagModal = useCallback((resource) => {
    setSelectedResource(resource);
    setActiveModal('tagsModal');
  }, [setActiveModal, setSelectedResource]);

  const handleDeleteResource = useCallback((resource) => {
    setSelectedResource(resource);
    setActiveModal('deleteResource');
  }, [setActiveModal, setSelectedResource]);

  const handleRecrawlResource = useCallback((resource) => {
    setSelectedResource(resource);
    setActiveModal('recrawlResource');
  }, [setActiveModal, setSelectedResource]);

  const meta = resources?.meta ?? {};
  const data = resources?.data ?? [];

  if (isLoading) {
    return (
      <Flex col center middle tall>
        <LoadingContainer />
        <Paragraph mt>Loading resource...</Paragraph>
      </Flex>
    );
  }

  if (error) {
    return (
      <p style={{ color: 'red' }}>
        Error fetching resources: {error.message || 'Unknown error occurred'}
      </p>
    );
  }

  return (
    <Flex col>
      {data.length === 0 ? (
        <Box border="dashed">
          <Flex col middle center>
            <Paragraph mb secondary>
              No documents have been added yet, please make sure folder is not empty in SharePoint.
            </Paragraph>
          </Flex>
        </Box>
      ) : (
        <>
          <Flex border xScroll>
            <Table loading={isLoading}>
              <TableHead>
                <OrderableTableRow
                  rows={[
                    { label: 'Name', sort: 'name' },
                    { label: 'Type', sort: 'type' },
                    { label: 'Status', sort: 'status' },
                    ...(canViewTag ? [{ label: 'Tags' }] : []),
                      ...(((canViewTag && canEditTag) || canEditResource) ? [null] : [])
                  ]}
                  {...order.props}
                />
              </TableHead>
              <TableBody>
                {data.map((resource) => (
                  <TableRow key={resource.id}>
                    <TableCell>{resource.name}</TableCell>
                    <TableCell>{resource.type}</TableCell>
                    <TableCell>
                      <Flex center>
                        <StateLevel level={resource.status} />
                        <span>{resource.status}</span>
                      </Flex>
                    </TableCell>
                    {canViewTag && (
                      <TableCell>
                        {resource.groups?.length
                          ? resource.groups.map(group => group.name).join(', ')
                          : 'No Tags'}
                      </TableCell>
                    )}
                    {((canViewTag && canEditTag) || canEditResource) && (
                      <TableActions>
                        <PermissionFilter can="view generative ai tag">
                          <PermissionFilter can="edit generative ai tag">
                            {resource.status === 'Success' && (
                              <Button
                                icon="tag"
                                onClick={() => handleTagModal(resource)}
                              >
                                Tags
                              </Button>
                            )}
                          </PermissionFilter>
                        </PermissionFilter>
                        <PermissionFilter can="edit generative ai resource">
                          {resource.status !== 'Manually Deleted' && (
                            <Button
                              variant="secondary"
                              icon="trash"
                              colour="danger"
                              onClick={() => handleDeleteResource(resource)}
                              disabled={shouldRefetch}
                            >
                              Delete
                            </Button>
                          )}
                          {resource.status === 'Manually Deleted' && (
                            <Button
                              icon="download"
                              disabled={shouldRefetch}
                              onClick={() => handleRecrawlResource(resource)}
                            >
                              Enable and Crawl
                            </Button>
                          )}
                        </PermissionFilter>
                      </TableActions>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Flex>
          {meta && <Pagination pagination={meta} {...pagination.props} />}
        </>
      )}
    </Flex>
  );
};

Resource.propTypes = {
  site: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  folder: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  state: PropTypes.shape({
    crawlResources: PropTypes.bool.isRequired,
    setSelectedResource: PropTypes.func.isRequired,
    setSelectedSite: PropTypes.func.isRequired,
    setSelectedFolder: PropTypes.func.isRequired,
    setActiveModal: PropTypes.func.isRequired,
    setCrawlResources: PropTypes.func.isRequired
  }).isRequired
};

export default Resource;